import {
  Box,
  Checkbox,
  DropdownSelect,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  LabeledControl,
  Paragraph,
  Popover,
  TextButton,
} from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';
import type { ComplianceFromCriteriaResult } from '../complianceFromCriteria';
import ComplianceAlerts from './ComplianceAlerts';
import type { FormState } from '../formState';
import { formatCurrencyAmount } from '../../../../utils/formatCurrencyAmount';
import { CREDIT_SCORE_HELP_TEXT, CRITERIA_SECTION_INTRO, EXPERIAN_CREDIT_SCORE_URL } from '../constants';

type FinancialExpectationsSectionProps = {
  formState: FormState;
  price: number;
  minIncomeDisplay: string;
  updateForm: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  compliance: ComplianceFromCriteriaResult;
};

export default function FinancialExpectationsSection({
  formState,
  price,
  minIncomeDisplay,
  updateForm,
  compliance,
}: FinancialExpectationsSectionProps) {
  const {
    creditAlert,
    incomeAlert,
    filteredIncomeOptions,
    filteredCreditOptions,
    isIncomeFieldEnabled,
    isCreditFieldEnabled,
  } = compliance;

  return (
    <Box>
      <Heading level={3} textStyle="heading-xs" css={{ marginBottom: 'tight' }}>
        Financial expectations
      </Heading>
      <Paragraph textStyle="body" css={{ marginBottom: 'default' }}>
        {CRITERIA_SECTION_INTRO}
      </Paragraph>
      <ComplianceAlerts creditAlert={creditAlert} incomeAlert={incomeAlert} />
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        {isIncomeFieldEnabled && (
          <>
            <FormField
              label={<Label>Minimum income-to-rent ratio</Label>}
              control={
                <DropdownSelect
                  options={filteredIncomeOptions}
                  value={formState.incomeToRent}
                  onOptionSelect={(option) => {
                    if (typeof option === 'object') updateForm('incomeToRent', option.value);
                  }}
                  showLabelForValue
                  css={{ width: '100%', boxShadowColor: 'border.subtle' }}
                />
              }
              description={<FormHelp>Rent is set at {formatCurrencyAmount(price)}/month</FormHelp>}
            />
            <LabeledControl
              label={<Label>Income-to-rent ratio is negotiable</Label>}
              control={
                <Checkbox
                  checked={formState.isIncomeToRentNegotiable}
                  onChange={(e) => updateForm('isIncomeToRentNegotiable', e.target.checked)}
                />
              }
            />
            <FormField
              label={<Label>Minimum monthly pre-tax income</Label>}
              control={<Input readOnly value={minIncomeDisplay} />}
            />
          </>
        )}
        {isCreditFieldEnabled && (
          <>
            <FormField
              label={
                <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'tighter' }}>
                  <Label>Minimum credit score</Label>
                  <Popover
                    tone="info"
                    placement="top"
                    header={<Heading level={6}>Putting credit scores in context</Heading>}
                    body={<Paragraph>{CREDIT_SCORE_HELP_TEXT}</Paragraph>}
                  />
                </Box>
              }
              control={
                <DropdownSelect
                  options={filteredCreditOptions}
                  value={formState.creditScore}
                  onOptionSelect={(option) => {
                    if (typeof option === 'object') updateForm('creditScore', option.value);
                  }}
                  showLabelForValue
                  css={{ width: '100%', boxShadowColor: 'border.subtle' }}
                />
              }
            />
            <LabeledControl
              label={<Label>Credit score is negotiable</Label>}
              control={
                <Checkbox
                  checked={formState.isCreditScoreNegotiable}
                  onChange={(e) => updateForm('isCreditScoreNegotiable', e.target.checked)}
                />
              }
            />

            <TextButton
              asChild
              textStyle="body-sm"
              icon={<IconExternalOutline />}
              iconPosition="end"
              css={{ justifyContent: 'start' }}
            >
              <a href={EXPERIAN_CREDIT_SCORE_URL} target="_blank" rel="noreferrer">
                How Experian talks about credit scores
              </a>
            </TextButton>
          </>
        )}
      </Box>
    </Box>
  );
}
